/* Director "Alertes de la journée" feed — surfaces:
 *  1. Recurrence alerts (students with 3+ consecutive unjustified absences)
 *  2. Repeated retards (≥3 lates in the window)
 *  3. Absence spikes (day where absent_pct > 1.5× the window average AND ≥5 absents)
 *
 * iter45 — Auto-refreshes when the SW dispatches a push with
 * event=absence_recurrence, and each alert has a "Contacter le parent"
 * action that surfaces mailto:/sms: links from the linked parents.
 */
import { useCallback, useEffect, useState } from 'react';
import { studentAttendanceApi } from '../../lib/api';
import { toast } from 'sonner';
import { usePushInbox } from '../../contexts/PushNotificationContext';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../ui/dialog';
import {
  Warning, Clock, Lightning, ArrowClockwise, ArrowRight,
  ChatCircle, EnvelopeSimple, Phone, User,
} from '@phosphor-icons/react';

const WINDOWS = [
  { value: 7, label: '7 j' },
  { value: 14, label: '14 j' },
  { value: 30, label: '30 j' },
];

const RELOAD_ON_EVENTS = new Set([
  'absence_recurrence',
  'absence_justified',
  'student_absent',
  'absence_reminder',
]);

const AlertsFeed = () => {
  const [days, setDays] = useState(7);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const [contactOpen, setContactOpen] = useState(false);
  const [contactStudent, setContactStudent] = useState(null); // {student_id, student_name}
  const [contactLoading, setContactLoading] = useState(false);
  const [contacts, setContacts] = useState([]);

  const { items: pushItems } = usePushInbox();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await studentAttendanceApi.directorAlertsFeed(days);
      setData(data);
    } catch (e) {
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [days]);

  useEffect(() => { load(); }, [load]);

  // Live refresh — reload the feed whenever a relevant push lands
  const latestPushId = pushItems[0]?.id;
  const latestPushEvent = pushItems[0]?.extra?.event;
  useEffect(() => {
    if (!latestPushId) return;
    if (RELOAD_ON_EVENTS.has(latestPushEvent)) {
      // Small debounce so React batches multiple pushes hitting at once
      const t = setTimeout(() => { load(); }, 200);
      return () => clearTimeout(t);
    }
  }, [latestPushId, latestPushEvent, load]);

  const openContacts = async (studentId, studentName) => {
    setContactStudent({ student_id: studentId, student_name: studentName });
    setContacts([]);
    setContactOpen(true);
    setContactLoading(true);
    try {
      const { data } = await studentAttendanceApi.parentContacts(studentId);
      setContacts(data.contacts || []);
      if ((data.contacts || []).length === 0) {
        toast.warning('Aucun contact parent enregistré pour cet élève.');
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur au chargement des contacts');
    } finally {
      setContactLoading(false);
    }
  };

  const totalAlerts = (data?.recurrences?.length || 0)
    + (data?.repeated_retards?.length || 0)
    + (data?.absence_spikes?.length || 0);

  return (
    <Card className="border border-[#E2E8F0] shadow-none" data-testid="alerts-feed-card">
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between gap-2 flex-wrap">
          <CardTitle className="font-outfit text-base flex items-center gap-2">
            <Warning size={20} className="text-amber-600" weight="duotone" />
            Alertes de la journée
            <span className="ml-2 text-xs bg-amber-100 text-amber-800 px-2 py-0.5 rounded-full font-bold" data-testid="alerts-feed-count">
              {totalAlerts}
            </span>
          </CardTitle>
          <div className="flex items-center gap-1">
            {WINDOWS.map((w) => (
              <button
                key={w.value}
                onClick={() => setDays(w.value)}
                className={`px-2 h-7 rounded text-xs font-medium border transition ${
                  days === w.value
                    ? 'bg-[#0052FF] text-white border-[#0052FF]'
                    : 'bg-white text-[#475569] border-[#E2E8F0] hover:border-slate-400'
                }`}
                data-testid={`alerts-window-${w.value}`}
              >
                {w.label}
              </button>
            ))}
            <Button size="sm" variant="ghost" onClick={load} disabled={loading} data-testid="alerts-refresh-btn">
              <ArrowClockwise size={14} />
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {loading ? (
          <p className="text-sm text-[#94A3B8] text-center py-4">Chargement…</p>
        ) : totalAlerts === 0 ? (
          <div className="p-6 text-center" data-testid="alerts-feed-empty">
            <p className="text-sm text-emerald-700 font-medium">Aucune alerte sur la période — tout va bien.</p>
          </div>
        ) : (
          <>
            {/* Recurrences */}
            {data.recurrences?.length > 0 && (
              <section data-testid="alerts-recurrences">
                <h3 className="text-xs uppercase font-bold text-red-700 mb-2 flex items-center gap-1">
                  <Warning size={14} weight="fill" />
                  Récurrences ({data.recurrences.length})
                </h3>
                <ul className="space-y-1.5">
                  {data.recurrences.slice(0, 5).map((r, i) => (
                    <li
                      key={`${r.student_id}-${r.date}-${i}`}
                      className="flex items-center gap-2 text-sm p-2 rounded bg-red-50 flex-wrap"
                      data-testid={`alerts-recurrence-row-${r.student_id}`}
                    >
                      <span className="font-semibold text-[#0F172A]">{r.student_name || '—'}</span>
                      <span className="text-xs text-[#64748B]">·</span>
                      <span className="font-mono text-xs text-[#475569]">{r.class_name}</span>
                      <span className="ml-auto text-xs text-red-700 font-bold">3+ absences non justifiées</span>
                      <Button
                        size="sm"
                        variant="outline"
                        className="h-7 text-[11px] border-red-300 text-red-700 hover:bg-red-100"
                        onClick={() => openContacts(r.student_id, r.student_name)}
                        data-testid={`alerts-contact-btn-${r.student_id}`}
                      >
                        <ChatCircle size={12} className="mr-1" />
                        Contacter le parent
                      </Button>
                    </li>
                  ))}
                </ul>
              </section>
            )}

            {/* Repeated retards */}
            {data.repeated_retards?.length > 0 && (
              <section data-testid="alerts-repeated-retards">
                <h3 className="text-xs uppercase font-bold text-amber-700 mb-2 flex items-center gap-1">
                  <Clock size={14} weight="fill" />
                  Retards répétés ({data.repeated_retards.length})
                </h3>
                <ul className="space-y-1.5">
                  {data.repeated_retards.slice(0, 5).map((r) => (
                    <li
                      key={r.student_id}
                      className="flex items-center gap-2 text-sm p-2 rounded bg-amber-50 flex-wrap"
                      data-testid={`alerts-retard-row-${r.student_id}`}
                    >
                      <span className="font-semibold text-[#0F172A]">{r.student_name || '—'}</span>
                      <span className="text-xs text-[#64748B]">·</span>
                      <span className="font-mono text-xs text-[#475569]">{r.class_name}</span>
                      <span className="ml-auto text-xs bg-amber-500 text-white px-2 py-0.5 rounded-full font-bold">
                        {r.retards_count} retards
                      </span>
                      <Button
                        size="sm"
                        variant="outline"
                        className="h-7 text-[11px] border-amber-400 text-amber-700 hover:bg-amber-100"
                        onClick={() => openContacts(r.student_id, r.student_name)}
                        data-testid={`alerts-contact-retard-btn-${r.student_id}`}
                      >
                        <ChatCircle size={12} className="mr-1" />
                        Contacter le parent
                      </Button>
                    </li>
                  ))}
                </ul>
              </section>
            )}

            {/* Absence spikes */}
            {data.absence_spikes?.length > 0 && (
              <section data-testid="alerts-absence-spikes">
                <h3 className="text-xs uppercase font-bold text-purple-700 mb-2 flex items-center gap-1">
                  <Lightning size={14} weight="fill" />
                  Pics d&apos;absence ({data.absence_spikes.length})
                </h3>
                <ul className="space-y-1.5">
                  {data.absence_spikes.slice(0, 5).map((s) => (
                    <li key={s.date} className="flex items-center gap-2 text-sm p-2 rounded bg-purple-50">
                      <span className="font-mono text-xs font-semibold">{s.date}</span>
                      <span className="text-xs text-[#64748B]">·</span>
                      <span className="text-xs text-[#475569]">
                        {s.absent} absents / {s.total} relevés
                      </span>
                      <span className="ml-auto text-xs bg-purple-600 text-white px-2 py-0.5 rounded-full font-bold">
                        {s.absent_pct}% (base {data.spike_baseline_pct}%)
                      </span>
                    </li>
                  ))}
                </ul>
              </section>
            )}

            <div className="pt-2 border-t border-[#E2E8F0]">
              <a
                href="/attendance-stats"
                className="text-xs text-[#0052FF] hover:underline flex items-center gap-1 font-medium"
                data-testid="alerts-feed-link-to-stats"
              >
                Voir toutes les statistiques
                <ArrowRight size={12} />
              </a>
            </div>
          </>
        )}
      </CardContent>

      {/* Contacts dialog */}
      <Dialog open={contactOpen} onOpenChange={setContactOpen}>
        <DialogContent className="max-w-md" data-testid="alerts-contacts-dialog">
          <DialogHeader>
            <DialogTitle className="font-outfit flex items-center gap-2">
              <ChatCircle size={20} className="text-[#0052FF]" weight="duotone" />
              Contacter le parent
            </DialogTitle>
            {contactStudent && (
              <p className="text-sm text-[#64748B] mt-1">{contactStudent.student_name}</p>
            )}
          </DialogHeader>
          <div className="space-y-3">
            {contactLoading ? (
              <p className="text-sm text-[#94A3B8] text-center py-4">Chargement…</p>
            ) : contacts.length === 0 ? (
              <p className="text-sm text-[#94A3B8] text-center py-4" data-testid="alerts-contacts-empty">
                Aucun contact parent enregistré pour cet élève.
              </p>
            ) : (
              contacts.map((c, i) => (
                <div
                  key={c.user_id || i}
                  className="border border-[#E2E8F0] rounded-md p-3 space-y-2"
                  data-testid={`alerts-contact-card-${i}`}
                >
                  <div className="flex items-center gap-2 font-semibold text-[#0F172A]">
                    <User size={16} className="text-[#64748B]" />
                    {c.name || 'Parent'}
                  </div>
                  {c.email && (
                    <a
                      href={`mailto:${c.email}?subject=${encodeURIComponent(
                        `Concernant ${contactStudent?.student_name || 'votre enfant'}`
                      )}`}
                      className="flex items-center gap-2 text-sm text-[#0052FF] hover:underline"
                      data-testid={`alerts-contact-email-${i}`}
                    >
                      <EnvelopeSimple size={14} />
                      {c.email}
                    </a>
                  )}
                  {c.phone && (
                    <div className="flex items-center gap-3 text-sm">
                      <a
                        href={`tel:${c.phone}`}
                        className="flex items-center gap-1 text-emerald-700 hover:underline"
                        data-testid={`alerts-contact-tel-${i}`}
                      >
                        <Phone size={14} />
                        {c.phone}
                      </a>
                      <a
                        href={`sms:${c.phone}?&body=${encodeURIComponent(
                          `Bonjour, concernant votre enfant ${contactStudent?.student_name || ''}. Cordialement.`
                        )}`}
                        className="text-xs text-[#0052FF] hover:underline"
                        data-testid={`alerts-contact-sms-${i}`}
                      >
                        Envoyer un SMS
                      </a>
                    </div>
                  )}
                  {!c.email && !c.phone && (
                    <p className="text-xs text-[#94A3B8]">Ni email ni téléphone renseigné.</p>
                  )}
                </div>
              ))
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setContactOpen(false)} data-testid="alerts-contacts-close-btn">
              Fermer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
};

export default AlertsFeed;
